
// Visual effects utilities for confetti, snowfall and other visual feedback
import confetti from "canvas-confetti";

// Default confetti configuration
const defaultConfettiConfig = {
  particleCount: 100,
  spread: 70,
  origin: { x: 0.5, y: 0.6 },
  colors: undefined, // Default colors
  shapes: undefined, // Default shapes
  scalar: 1.0, // Size multiplier
  gravity: 1.0, // Gravity multiplier
  drift: 0, // Horizontal drift
  ticks: 200 // Animation length
};

// Confetti configuration types
export interface ConfettiConfig {
  particleCount?: number;
  spread?: number;
  origin?: { x: number, y: number };
  colors?: string[];
  shapes?: ('square' | 'circle')[];
  scalar?: number;
  gravity?: number;
  drift?: number;
  ticks?: number;
}

// Snowfall configuration types
export interface SnowfallConfig {
  particleCount?: number;
  speed?: number;
  wind?: number;
  duration?: number;
  opacity?: number;
}

// Default snowfall configuration
const defaultSnowfallConfig: SnowfallConfig = {
  particleCount: 50,
  speed: 0.5,
  wind: 0,
  duration: 6000,
  opacity: 0.8
};

/**
 * Trigger confetti effect with custom configuration
 */
export const triggerConfettiEffect = (config: ConfettiConfig = {}): void => {
  // Merge default and custom configuration
  const mergedConfig = {
    ...defaultConfettiConfig,
    ...config
  };
  
  // Launch confetti with merged configuration
  confetti(mergedConfig);
};

/**
 * Trigger confetti from a specific HTML element with custom configuration
 */
export const triggerConfettiFromElement = (
  element: HTMLElement | null, 
  config: ConfettiConfig = {}
): void => {
  if (!element) return;
  
  const rect = element.getBoundingClientRect();
  const x = (rect.left + rect.right) / 2;
  const y = (rect.top + rect.bottom) / 2;
  
  // Calculate origin relative to viewport
  const originX = x / window.innerWidth;
  const originY = y / window.innerHeight;
  
  // Merge with default config and provided config, overriding origin
  const mergedConfig = {
    ...defaultConfettiConfig,
    ...config,
    origin: { x: originX, y: originY }
  };
  
  // Trigger confetti from the element's position with merged configuration
  confetti(mergedConfig);
};

/**
 * Trigger a realistic snowfall effect
 */
export const triggerSnowfall = (config: SnowfallConfig = {}): () => void => {
  // Merge default and custom configuration
  const mergedConfig = {
    ...defaultSnowfallConfig,
    ...config
  };
  
  // Parameters for snowfall
  const { particleCount, speed, wind, duration, opacity } = mergedConfig;
  
  // Flag to control animation loop
  let isActive = true;
  
  // Calculate end time based on provided duration
  const endTime = Date.now() + (duration || 6000);
  
  // Create function to animate the snowflakes
  const animateSnowflakes = () => {
    const now = Date.now();
    const remainingTime = Math.max(0, endTime - now);
    
    // Calculate current opacity based on remaining time
    const currentOpacity = remainingTime === 0 ? 0 : 
      opacity * (remainingTime / (duration || 6000));
    
    // Snowflake parameters
    (confetti as any)({
      particleCount: particleCount || 1,
      angle: 90,
      spread: 45,
      origin: { x: Math.random(), y: -0.1 },
      gravity: speed || 0.5,
      drift: wind || 0,
      ticks: 400,
      decay: 0.95,
      scalar: 0.8,
      shapes: ['circle'],
      colors: ['#ffffff'],
      opacity: currentOpacity
    });
    
    // Stop animation if duration has passed
    if (now > endTime) {
      isActive = false;
    }
    
    // Continue animation if still active
    if (isActive) {
      requestAnimationFrame(animateSnowflakes);
    }
  };
  
  // Start animation loop
  animateSnowflakes();
  
  // Return a function to stop the animation early if needed
  return () => {
    isActive = false;
  };
};

/**
 * Predefined confetti patterns
 */
export const CONFETTI_PATTERNS: Record<string, ConfettiConfig> = {
  FIREWORKS: {
    particleCount: 100,
    spread: 70,
    origin: { x: 0.5, y: 0.6 }
  },
  PARTY: {
    particleCount: 150,
    spread: 100,
    origin: { x: 0.5, y: 0.5 },
    colors: ['#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3'],
    ticks: 300
  },
  CELEBRATION: {
    particleCount: 200,
    spread: 160,
    origin: { x: 0.5, y: 0.5 },
    shapes: ['square', 'circle'] as ('square' | 'circle')[],
    gravity: 0.8,
    ticks: 150
  },
  SUBTLE: {
    particleCount: 30,
    spread: 50,
    origin: { x: 0.5, y: 0.5 },
    colors: ['#9c27b0', '#673ab7', '#3f51b5'],
    ticks: 100
  }
};
